import { ChangeEvent, useEffect } from "react";
import { Box, Grid, MenuItem } from "@mui/material";
import { useResetInputs } from "src/Contexts";
import { useAuthContext } from "src/Contexts/AuthContext/authContext";
import { useContextReportsProduction } from "src/Contexts/ReportProduction/ReportsProductionContextContext";
import { InputsConfirmationPayment } from "src/Contexts/ReportProduction/ReportsProductionTypes";
import { AppTextField } from "src/Pages/components/AppFormComponents/AppTextField";
import { ComponentPrivate } from "src/Pages/components/ComponentPrivate/ComponentPrivate";
import { DividerCore } from "src/Pages/components/DividerCore/DividerCore";
import { DrawerFilterReport } from "src/Pages/components/DrawerFilterReport/DrawerFilterReport";

import { UseReportConfirmationPayment } from "./Form/Hooks/UseReportConfirmationPayment";
import { useConfigPageContext } from "src/Contexts/configPagesContext/configPagesContext";
import { AutoCompleteCorretorGeneric } from "src/Pages/components/AutoCompleted/AutoCompleteCorretor";
import { AutoCompleteSupervisorReports } from "src/Pages/components/AutoCompleted/AutoCompleteSupervisorReposrts";
import { AutoCompleteGerenteReports } from "src/Pages/components/AutoCompleted/AutoCompleteGerenteReports";

type Props = {
  isUserProducer: boolean;
};

export const DrawerFilterConfirmationPayment = ({ isUserProducer }: Props) => {
  const { setActive_incluir, active_incluir } = useConfigPageContext();
  const { handleSubmit } = UseReportConfirmationPayment();
  const { resetInputs } = useResetInputs(true);
  const { userPerfil } = useAuthContext();

  const { setInputsConfirmationPayment, inputsConfirmationPayment } =
    useContextReportsProduction();

  const reportPersonValue =
    inputsConfirmationPayment.type_report === "ALL"
      ? "ALL"
      : inputsConfirmationPayment.funcao_produtor;

  const handleChangeReportPerson = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value as
      | "ALL"
      | "CORRETOR"
      | "SUPERVISOR"
      | "GERENTE";

    if (selectedValue === "ALL") {
      setInputsConfirmationPayment((prevValue) => ({
        ...prevValue,
        type_report: "ALL",
        funcao_produtor: "",
      }));
      return;
    }

    setInputsConfirmationPayment((prevValue) => ({
      ...prevValue,
      type_report: "UNIQUE",
      funcao_produtor: selectedValue,
    }));
  };

  useEffect(() => setActive_incluir(true), [active_incluir]);

  useEffect(() => {
    if (userPerfil?.type_user !== "PRODUCER") return;

    setInputsConfirmationPayment((prevValue) => ({
      ...prevValue,
      type_report: "UNIQUE",
      funcao_produtor: "CORRETOR",
    }));
  }, [userPerfil?.type_user]);

  const handleChange =
    (prop: keyof InputsConfirmationPayment) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setInputsConfirmationPayment({
        ...inputsConfirmationPayment,
        [prop]: event.target.value,
      });
    };

  return (
    <DrawerFilterReport onClear={resetInputs} onApply={handleSubmit}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          "& .MuiAutocomplete-root": { margin: 0 },
        }}
      >
        <Box>
          <DividerCore title="Período" sxStyle={{ margin: "0 0 16px 0" }} />

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={6}>
              <AppTextField
                fullWidth
                label="Data inicial"
                type="date"
                variant="outlined"
                sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                value={inputsConfirmationPayment?.start_date}
                onChange={handleChange("start_date")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <AppTextField
                fullWidth
                label="Data final"
                type="date"
                variant="outlined"
                sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                value={inputsConfirmationPayment?.end_date}
                onChange={handleChange("end_date")}
              />
            </Grid>
          </Grid>
        </Box>

        <ComponentPrivate isView={userPerfil?.type_user !== "PRODUCER"}>
          <Box>
            <DividerCore
              title="Tipo de relatório"
              sxStyle={{ margin: "0 0 16px 0" }}
            />
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={6}>
                <AppTextField
                  fullWidth
                  select
                  id="tipo_relatorio_pessoa"
                  label="Relatório / Pessoa"
                  variant="outlined"
                  sxStyle={{ margin: 0 }}
                  value={reportPersonValue}
                  onChange={handleChangeReportPerson}
                  disabled={isUserProducer}
                >
                  <MenuItem value="ALL">Todos</MenuItem>
                  <MenuItem value="CORRETOR">Corretor</MenuItem>
                  <MenuItem value="SUPERVISOR">Supervisor</MenuItem>
                  <MenuItem value="GERENTE">Gerente</MenuItem>
                </AppTextField>
              </Grid>
              {inputsConfirmationPayment.type_report === "UNIQUE" &&
                inputsConfirmationPayment.funcao_produtor === "CORRETOR" && (
                  <Grid item xs={12} sm={6}>
                    <AutoCompleteCorretorGeneric
                      fullWidth
                      label="Corretor"
                      variant="outlined"
                    />
                  </Grid>
                )}
              {inputsConfirmationPayment.type_report === "UNIQUE" &&
                inputsConfirmationPayment.funcao_produtor === "SUPERVISOR" && (
                  <Grid item xs={12} sm={6}>
                    <AutoCompleteSupervisorReports
                      fullWidth
                      label="Supervisor"
                      variant="outlined"
                    />
                  </Grid>
                )}
              {inputsConfirmationPayment.type_report === "UNIQUE" &&
                inputsConfirmationPayment.funcao_produtor === "GERENTE" && (
                  <Grid item xs={12} sm={6}>
                    <AutoCompleteGerenteReports
                      fullWidth
                      label="Gerente"
                      variant="outlined"
                    />
                  </Grid>
                )}
            </Grid>
          </Box>
        </ComponentPrivate>
      </Box>
    </DrawerFilterReport>
  );
};
